import { wrapLines } from "@/lib/zikirCard";
import { HAJJ_UI } from "@/data/ui/hajjTimelineUi";
import { getStations } from "@/lib/manasikContent";
import { REMINDER_DAYS, REMINDER_UI } from "@/data/manasikReminders";
import { fill } from "@/lib/utils";
import { logWarn } from "@/lib/log";

// Shareable emerald+gold "today's manasik" card (canvas) → Web Share API, WhatsApp + download fallback.
const W = 1080;
const PAD = 90;
const CONTENT_W = W - PAD * 2;
const GOLD = "#E6C25C";
const GOLD_DEEP = "#D4AF37";
const WHITE = "#ffffff";
const SUB = "rgba(255,255,255,0.82)";

export async function shareHajjCard({ dateLine, title, body, arabic, translit, meaning, name, labels }) {
  try {
    await document.fonts.ready;
    await document.fonts.load('64px "UthmanicHafs"');
  } catch (e) { logWarn("fonts not ready, using system fonts:", e); }

  const m = document.createElement("canvas").getContext("2d");
  const AR_FONT = (px) => `${px}px UthmanicHafs, "Traditional Arabic", Georgia, serif`;
  const SERIF = (px, w = "") => `${w} ${px}px "Playfair Display", Georgia, serif`.trim();
  const SANS = (px, w = "") => `${w} ${px}px "Plus Jakarta Sans", Arial, sans-serif`.trim();

  const sections = [];
  sections.push({ lines: wrapLines(m, dateLine.toUpperCase(), SANS(28, "bold"), CONTENT_W), font: SANS(28, "bold"), color: GOLD, lh: 40, gap: 26, spacing: true });
  sections.push({ lines: wrapLines(m, title, SERIF(56, "bold"), CONTENT_W), font: SERIF(56, "bold"), color: WHITE, lh: 70, gap: 28 });
  sections.push({ lines: wrapLines(m, body, SANS(32), CONTENT_W), font: SANS(32), color: SUB, lh: 48, gap: 44 });
  if (arabic) {
    sections.push({ type: "divider", gap: 44 });
    sections.push({ lines: [labels.duaLabel.toUpperCase()], font: SANS(24, "bold"), color: GOLD, lh: 34, gap: 64, spacing: true });
    sections.push({ rtl: true, lines: wrapLines(m, arabic, AR_FONT(64), CONTENT_W), font: AR_FONT(64), color: WHITE, lh: 104, gap: 30 });
    if (translit) sections.push({ lines: wrapLines(m, translit, SANS(28), CONTENT_W), font: `italic 28px "Plus Jakarta Sans", Arial, sans-serif`, color: SUB, lh: 42, gap: 30 });
    if (meaning) sections.push({ lines: wrapLines(m, meaning, SANS(32), CONTENT_W), font: SANS(32), color: WHITE, lh: 48, gap: 20 });
  }

  const TOP = 320;
  const FOOTER = 230;
  let h = TOP;
  for (const s of sections) h += s.type === "divider" ? s.gap : s.lines.length * s.lh + s.gap;
  h += FOOTER;
  const H = Math.max(1350, Math.ceil(h));

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#0a3b2b");
  g.addColorStop(0.5, "#0f5a3f");
  g.addColorStop(1, "#0a3b2b");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  const rg = ctx.createRadialGradient(W - 120, 120, 40, W - 120, 120, 460);
  rg.addColorStop(0, "rgba(230,194,92,0.22)");
  rg.addColorStop(1, "rgba(230,194,92,0)");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = GOLD_DEEP;
  ctx.lineWidth = 8;
  ctx.strokeRect(44, 44, W - 88, H - 88);
  ctx.lineWidth = 2;
  ctx.strokeRect(64, 64, W - 128, H - 128);

  ctx.textAlign = "center";
  ctx.fillStyle = GOLD;
  ctx.font = AR_FONT(48);
  ctx.direction = "rtl";
  ctx.fillText("بِسْمِ اللَّهِ", W / 2, 155);
  ctx.direction = "ltr";
  ctx.fillStyle = SUB;
  ctx.font = SANS(26);
  ctx.fillText((labels.brand || "Manasik").toUpperCase(), W / 2, 205);

  let y = TOP;
  for (const s of sections) {
    if (s.type === "divider") {
      ctx.strokeStyle = "rgba(230,194,92,0.5)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(W / 2 - 70, y - s.gap / 2);
      ctx.lineTo(W / 2 + 70, y - s.gap / 2);
      ctx.stroke();
      y += s.gap / 2;
      continue;
    }
    ctx.textAlign = "center";
    ctx.direction = s.rtl ? "rtl" : "ltr";
    ctx.fillStyle = s.color;
    ctx.font = s.font;
    if ("letterSpacing" in ctx) ctx.letterSpacing = s.spacing ? "4px" : "0px";
    for (const ln of s.lines) { ctx.fillText(ln, W / 2, y); y += s.lh; }
    y += s.gap;
  }
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";

  ctx.direction = "ltr";
  ctx.textAlign = "center";
  if (name) {
    ctx.fillStyle = GOLD;
    ctx.font = SANS(30, "600");
    ctx.fillText(fill(labels.byLabel, { name }), W / 2, H - 135);
  }
  ctx.fillStyle = SUB;
  ctx.font = SANS(28);
  const footLines = wrapLines(ctx, labels.footer || "Manasik — Panduan Umrah & Haji", SANS(28), CONTENT_W).slice(0, 2);
  footLines.forEach((ln, i) => ctx.fillText(ln, W / 2, H - 80 - (footLines.length - 1 - i) * 36));

  const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
  const file = new File([blob], "manasik-hari-ini.png", { type: "image/png" });
  const shareText = labels.shareText || "";
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title, text: shareText });
      return "shared";
    }
  } catch (e) { logWarn("Web Share cancelled/unsupported, falling back:", e); }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "manasik-hari-ini.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, "_blank");
  return "downloaded";
}

const DAY_KEY = { "8": "day8", "9": "day9", "10": "day10", tasyrik: "daytasyrik", wada: "daywada" };
const REMINDER_DAY_FOR = { "8": 8, "9": 9, "10": 10, tasyrik: 11 };

// Build + share the card for a timeline day (dateLine = "Hari ini 9 Dzulhijjah 1448 H" or the day label).
export function shareDayCard({ tlDay, dateLine, tc, name, brand = "Manasik" }) {
  const rem = REMINDER_DAYS.find((d) => d.day === REMINDER_DAY_FOR[tlDay]);
  const stations = getStations().filter((s) => s.day === tlDay);
  const dua = stations.find((s) => s.arabic);
  const title = rem ? tc(rem.title) : tc(HAJJ_UI[DAY_KEY[tlDay]]);
  const body = rem ? tc(rem.body) : stations[0] ? tc(stations[0].body[0]) : "";
  return shareHajjCard({
    dateLine: dateLine || tc(HAJJ_UI.pageTitle),
    title, body,
    arabic: dua?.arabic, translit: dua?.translit, meaning: dua?.dua ? tc(dua.dua) : "",
    name,
    labels: {
      brand,
      duaLabel: tc(REMINDER_UI.shareDua),
      byLabel: tc(REMINDER_UI.shareBy),
      shareText: fill(tc(REMINDER_UI.shareText), { date: dateLine || tc(HAJJ_UI.pageTitle), title }),
      footer: `${brand} · ${tc(HAJJ_UI.pageTitle)}`,
    },
  });
}
